/**
 * @file useLinks Hook
 * @description React Query hooks for investment link operations.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';

import {
  linksService,
  type CreateLinkDto,
  type UpdateLinkDto,
  type LinksListResponse,
  type LinkWithWarning,
} from '../services/links.service';
import { unwrapResponse } from '../types';

import type { InvestmentLink } from '@medsuite/models';

/**
 * Query key factory for links
 */
export const linkKeys = {
  all: ['links'] as const,
  lists: () => [...linkKeys.all, 'list'] as const,
  list: (investmentId: number) => [...linkKeys.lists(), investmentId] as const,
};

/**
 * Fetch all links for an investment
 */
export function useLinks(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentLink[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentLink[], Error>({
    queryKey: linkKeys.list(investmentId),
    queryFn: async () => {
      const response = await linksService.getAll(investmentId);
      // Extract data from success wrapper
      const responseData = unwrapResponse<LinksListResponse>(response.data);
      return responseData.links || [];
    },
    enabled: investmentId > 0,
    ...options,
  });
}

/**
 * Create a new link
 */
export function useCreateLink(
  investmentId: number,
  options?: UseMutationOptions<LinkWithWarning, Error, CreateLinkDto>
) {
  const queryClient = useQueryClient();

  return useMutation<LinkWithWarning, Error, CreateLinkDto>({
    mutationFn: async (data) => {
      const response = await linksService.create(investmentId, data);
      return unwrapResponse<LinkWithWarning>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linkKeys.list(investmentId) });
    },
    ...options,
  });
}

/**
 * Update an existing link
 */
export function useUpdateLink(
  investmentId: number,
  options?: UseMutationOptions<
    LinkWithWarning,
    Error,
    { linkId: number; data: UpdateLinkDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<
    LinkWithWarning,
    Error,
    { linkId: number; data: UpdateLinkDto }
  >({
    mutationFn: async ({ linkId, data }) => {
      const response = await linksService.update(investmentId, linkId, data);
      return unwrapResponse<LinkWithWarning>(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linkKeys.list(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete a link
 */
export function useDeleteLink(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (linkId) => {
      await linksService.delete(investmentId, linkId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: linkKeys.list(investmentId) });
    },
    ...options,
  });
}
